import { areFingerprintsEqual, fingerprintValue } from '../../../shared/lib/stableFingerprint';

export interface ValidationContextInput {
  projectKey: string | null;
  documentName: string | null;
  documentPath: string | null;
  /** Verified setup/profile revision; `null` while nothing is verified. */
  verifiedRevision: string | null;
  selectedValidationIds: number[];
  /** Absent and `null` both mean "this caller has no settings to describe". */
  settings?: Record<string, unknown> | null;
}

/** The two report fields that together decide whether a report is current. */
export interface ValidationReportContext {
  reportContextFingerprint: string | null;
  reportStale: boolean;
}

/**
 * The live setup as its consumers already hold it. The settings snapshot is
 * passed in rather than read here so this module stays free of store access.
 */
export interface LiveValidationContext {
  activeProjectKey: string | null;
  documentName: string | null;
  documentPath: string | null;
  verifiedRevision: string | null;
  selectedValidationIds: number[];
  /** Plain settings bag; callers spread their own snapshot so this stays store-agnostic. */
  settingsSnapshot: Record<string, unknown>;
  reconnectGapMarginMillimeters: number;
  mappingOverrides?: Record<string, string>;
}

/** The canonical settings a report is described by, and its context fingerprint. */
export interface ValidationContextDescriptor {
  settings: Record<string, unknown>;
  fingerprint: string;
}

const ContextPrefix = 'vc1';

/**
 * Names the exact context a validation report describes: document identity,
 * verified setup revision, selected checks and settings. The model version is
 * deliberately absent: genuine edits already invalidate through the ModelChanged
 * event bus, and hashing the version here would let the plugin's own repair
 * writes (which bump it) flip a freshly refreshed report to stale.
 */
export const buildValidationContextFingerprint = (input: ValidationContextInput): string =>
  fingerprintValue(ContextPrefix, {
    projectKey: input.projectKey,
    documentName: input.documentName,
    documentPath: input.documentPath,
    verifiedRevision: input.verifiedRevision,
    selectedValidationIds: [...input.selectedValidationIds].sort((left, right) => left - right),
    settings: input.settings ?? {},
  });

/**
 * Composes the live setup into the two artifacts a report is described by, so
 * the runner that stores a fingerprint and the page that compares against it
 * can never drift apart.
 */
export const describeValidationContext = (
  context: LiveValidationContext
): ValidationContextDescriptor => {
  const settings: Record<string, unknown> = {
    ...context.settingsSnapshot,
    reconnectGapMarginMillimeters: context.reconnectGapMarginMillimeters,
    ...(context.mappingOverrides ? { mappingOverrides: context.mappingOverrides } : {}),
  };

  return {
    settings,
    fingerprint: buildValidationContextFingerprint({
      projectKey: context.activeProjectKey,
      documentName: context.documentName,
      documentPath: context.documentPath,
      verifiedRevision: context.verifiedRevision,
      selectedValidationIds: context.selectedValidationIds,
      settings,
    }),
  };
};

/** Only a report captured in exactly this context, and never invalidated, is current. */
export const isValidationReportCurrent = (
  context: ValidationReportContext,
  currentFingerprint: string | null
): boolean =>
  !context.reportStale &&
  areFingerprintsEqual(context.reportContextFingerprint, currentFingerprint);
